import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { EASE, MaskedLines, Fade } from "./motion";

const HEADLINE = [
    { text: "Engineering", accent: false },
    { text: "talent,", accent: false },
    { text: "engineered.", accent: true },
];

const NODES = [
    { x: 30, label: "CAMPUS", sub: "ENROLMENT — YEAR 00", stage: "STAGE A", hot: false },
    { x: 480, label: "CAPABILITY", sub: "THE QRUS SYSTEM — YR 01–04", stage: "STAGE B", hot: true },
    { x: 930, label: "INDUSTRY", sub: "EMPLOYABILITY — YEAR 04+", stage: "STAGE C", hot: false },
];

const PipelineDiagram = () => (
    <svg viewBox="0 0 1200 250" className="h-auto w-full min-w-[640px] font-sans" role="img" aria-label="Diagram: campus to capability to industry">
        {NODES.map((n) => (
            <g key={n.label}>
                <line x1={n.x + 120} y1={30} x2={n.x + 120} y2={78} stroke="#6D8196" strokeOpacity="0.35" strokeDasharray="2 5" />
                <text x={n.x + 120} y={22} textAnchor="middle" fontSize="9" letterSpacing="2.5" fill="#6D8196">
                    {n.stage}
                </text>
            </g>
        ))}

        <motion.line x1={270} y1={120} x2={468} y2={120} stroke="#0047AB" strokeWidth="1.5"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, ease: EASE, delay: 1.3 }} />
        <motion.line x1={720} y1={120} x2={918} y2={120} stroke="#0047AB" strokeWidth="1.5"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, ease: EASE, delay: 1.55 }} />
        <motion.polygon points="466,120 480,113 480,127" fill="#0047AB"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 0.4 }} />
        <motion.polygon points="916,120 930,113 930,127" fill="#0047AB"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4, duration: 0.4 }} />

        {[312, 354, 396, 762, 804, 846].map((x) => (
            <line key={x} x1={x} y1={116} x2={x} y2={124} stroke="#6D8196" strokeOpacity="0.4" />
        ))}

        <motion.rect width={7} height={7} y={116.5} fill="#82C8E5"
            initial={{ x: 272, opacity: 0 }}
            animate={{ x: [272, 458], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "linear", times: [0, 0.12, 0.88, 1], delay: 2.4 }} />
        <motion.rect width={7} height={7} y={116.5} fill="#82C8E5"
            initial={{ x: 722, opacity: 0 }}
            animate={{ x: [722, 908], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "linear", times: [0, 0.12, 0.88, 1], delay: 3.4 }} />

        {NODES.map((n, i) => (
            <motion.g key={n.label}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 1.1 + i * 0.18 }}>
                <rect x={n.x} y={80} width={240} height={80}
                    fill={n.hot ? "#0047AB" : "#FFFFFF"}
                    stroke={n.hot ? "#0047AB" : "#0B1220"} strokeOpacity={n.hot ? 1 : 0.25} />
                <rect x={n.x - 5} y={75} width={10} height={10} fill="none" stroke={n.hot ? "#82C8E5" : "#0047AB"} strokeOpacity="0.6" />
                <rect x={n.x + 235} y={155} width={10} height={10} fill="none" stroke={n.hot ? "#82C8E5" : "#0047AB"} strokeOpacity="0.6" />
                <text x={n.x + 120} y={114} textAnchor="middle" fontSize="16" fontWeight="600" letterSpacing="4"
                    fill={n.hot ? "#FFFFFF" : "#0B1220"}>
                    {n.label}
                </text>
                <text x={n.x + 120} y={138} textAnchor="middle" fontSize="8.5" letterSpacing="2"
                    fill={n.hot ? "#82C8E5" : "#6D8196"}>
                    {n.sub}
                </text>
            </motion.g>
        ))}

        <text x={30} y={218} fontSize="9" letterSpacing="2.5" fill="#6D8196">FIG.01 — TALENT PIPELINE</text>
        <text x={1170} y={218} textAnchor="end" fontSize="9" letterSpacing="2.5" fill="#6D8196">CYCLE: 4 YEARS</text>
    </svg>
);

export default function Hero() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 40, damping: 18 });
    const sy = useSpring(my, { stiffness: 40, damping: 18 });

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const headY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const gridY = useTransform(scrollYProgress, [0, 1], [0, -80]);

    const onMouseMove = (e) => {
        mx.set((e.clientX / window.innerWidth - 0.5) * 22);
        my.set((e.clientY / window.innerHeight - 0.5) * 14);
    };

    return (
        <section
            id="top"
            ref={ref}
            onMouseMove={onMouseMove}
            className="blueprint-grid relative flex min-h-screen flex-col overflow-hidden bg-paper pt-24 md:pt-36"
            data-testid="hero-section"
        >
            <div className="mx-auto w-full max-w-[1440px] flex-1 px-6 md:px-10 lg:px-16">
                <div className="flex items-center justify-between border-b border-ink/10 pb-5">
                    <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="text-[10px] uppercase tracking-[0.3em] text-slate md:text-xs"
                    >
                        QRUS — Engineering Talent Systems
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                        className="hidden text-[10px] uppercase tracking-[0.3em] text-slate md:block md:text-xs"
                    >
                        Pronounced &lsquo;Curious&rsquo;
                    </motion.p>
                </div>

                <motion.div style={{ y: headY }}>
                    <MaskedLines
                        immediate
                        delay={0.35}
                        stagger={0.14}
                        className="mt-12 md:mt-16"
                        lineClassName="font-display text-[clamp(2.8rem,12vw,9rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-ink"
                        lines={HEADLINE.map((l) => (
                            <span key={l.text}>
                                {l.accent ? <span className="text-cobalt">{l.text}</span> : l.text}
                            </span>
                        ))}
                    />

                    <div className="mt-12 flex flex-col gap-10 md:mt-16 md:flex-row md:items-end md:justify-between">
                        <Fade delay={1.0} className="max-w-md">
                            <p className="text-base leading-relaxed text-slate md:text-lg">
                                QRUS is an AI-enabled system that develops engineering students
                                into industry-ready professionals — across all four years of
                                their degree.
                            </p>
                        </Fade>
                        <Fade delay={1.15} className="flex flex-wrap items-center gap-4">
                            <button
                                data-testid="hero-cta-primary"
                                onClick={() => scrollToId("contact")}
                                className="group flex items-center gap-2.5 bg-cobalt px-7 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-navy"
                            >
                                Partner with QRUS
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                            <button
                                data-testid="hero-cta-secondary"
                                onClick={() => scrollToId("about")}
                                className="group flex items-center gap-2.5 border border-ink/20 bg-white/50 px-7 py-4 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:border-cobalt hover:text-cobalt"
                            >
                                Explore the system
                                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                            </button>
                        </Fade>
                    </div>
                </motion.div>
            </div>

            <motion.div style={{ y: gridY }} className="relative mt-16 md:mt-20">
                <div className="mx-auto max-w-[1440px] px-6 pb-14 md:px-10 lg:px-16">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.0 }}
                        className="relative border border-ink/10 bg-white/70 backdrop-blur-sm"
                    >
                        <span className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-cobalt" />
                        <span className="absolute -right-px -top-px h-3 w-3 border-r-2 border-t-2 border-cobalt" />
                        <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-cobalt" />
                        <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-cobalt" />
                        <motion.div style={{ x: sx, y: sy }} className="overflow-x-auto px-4 py-8 md:px-10 md:py-10">
                            <PipelineDiagram />
                        </motion.div>
                        <span className="absolute right-4 top-4 hidden text-[9px] uppercase tracking-[0.3em] text-slate [writing-mode:vertical-rl] md:block">
                            QRUS / SYS.01
                        </span>
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
}
